import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useKeyplayApi} from '../../context/ApiContext';
import {CrmField, CrmIntegrationType} from '../../shared/integrations/crm';
import {useIntegrations} from './integrations';
import {GetTaskResponse} from '../../shared/api/definitions/tasks';
import {useTask} from './tasks';
import {isCrmIntegrationResponse} from '../../shared/api/definitions/integration';

export function useConnectedCrmIntegration() {
  const {data, isPending, isError} = useIntegrations({});
  if (isPending || isError) {
    return;
  }

  return data.find(isCrmIntegrationResponse);
}

export function useCrmFields(name: CrmIntegrationType) {
  const makeApiCall = useKeyplayApi();

  return useQuery({
    queryKey: ['integration', 'crmFields', name],
    queryFn: () => makeApiCall<CrmField[]>(`/integrations/${name}/crmFields`),
    refetchOnWindowFocus: false,
  });
}

export function useIsCrmSyncing(options?: {
  onSyncComplete?: (task: NonNullable<GetTaskResponse>) => void;
}) {
  const integration = useConnectedCrmIntegration();

  const {isTaskInProgress} = useTask(
    integration
      ? {
          type:
            integration.type === 'hubspot'
              ? 'hubSpotFullRun'
              : 'salesforceFullRun',
        }
      : 'skipToken',
    {onTaskComplete: options?.onSyncComplete}
  );

  return isTaskInProgress;
}

export function useQueueFullCrmRun() {
  const makeApiCall = useKeyplayApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: CrmIntegrationType) =>
      makeApiCall(
        `/integrations/${name}/enrich`,
        {
          method: 'POST',
        },
        {
          toastOnError: true,
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['taskStatus'],
      });
    },
  });
}
